import routes from "./routes";
import { useLocation, useRoutes } from "react-router-dom";
import type { CategoryBackendResponse } from "./types";
import {
  clearCart,
  fetchProductAvailability,
  getAllProducts,
  setDamageWaiver,
  setSetupSurface,
  setShoppingId,
} from "./reducers/actions";
import { BASE_URL } from "./index";
import { useDispatch, useSelector } from "react-redux";
import { getDateRange } from "./reducers/date";
import { useEffect, useState } from "react";
import { getShoppingId } from "./reducers/customer";
import { getCartItems } from "./reducers/cart";
import slugify from "slugify";
import { captureException } from "@sentry/react";

const App = () => {
  const dispatch = useDispatch<any>();
  const { startInstant, endInstant } = useSelector(getDateRange);
  const cartItems = useSelector(getCartItems);
  const shoppingId = useSelector(getShoppingId);
  const [updateCartTimer, setUpdateCartTimer] = useState<any>(null);
  const { pathname } = useLocation();

  const updateCart = (
    shoppingId: string,
    cart: { productId: number; amount: number }[],
    startInstant: Date | null,
    endInstant: Date | null,
  ) => {
    fetch(`${BASE_URL}/cart/update`, {
      method: "POST",
      body: JSON.stringify({
        shoppingId: shoppingId,
        cart: cart,
        startDateTime: startInstant?.toISOString(),
        endDateTime: endInstant?.toISOString(),
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }).catch((e) => {
      captureException(e);
      console.error(e);
    });
  };

  useEffect(() => {
    if (!shoppingId) {
      return;
    }
    const cart: { productId: number; amount: number }[] = [];
    if (cartItems) {
      cartItems.forEach((item) => {
        cart.push({
          productId: item.id,
          amount: item.quantity,
        });
      });
    }
    if (cart.length === 0 && !startInstant && !endInstant) {
      return;
    }
    if (updateCartTimer) {
      clearTimeout(updateCartTimer);
    }
    const timer = setTimeout(() => {
      updateCart(shoppingId, cart, startInstant, endInstant);
    }, 250);
    setUpdateCartTimer(timer);
    return () => {
      clearTimeout(timer);
      setUpdateCartTimer(null);
    };
  }, [cartItems, startInstant, endInstant]);

  useEffect(() => {
    if (pathname === "/") {
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (shoppingId) {
      return;
    }
    try {
      fetch(`${BASE_URL}/cart/start`)
        .then((response) => response.json() as Promise<{ shoppingId: string }>)
        .then((data: { shoppingId: string }) => {
          dispatch(setShoppingId(data.shoppingId));
        });
    } catch (e) {
      captureException(e);
      console.error(e);
    }
  }, [shoppingId]);

  const requestStoreInfo = async () => {
    if (startInstant && endInstant) {
      dispatch(fetchProductAvailability(startInstant, endInstant));
    } else {
      dispatch(clearCart());
    }

    try {
      // const siteInfoResponse = await fetch(`${base_url}site`, {
      //   method: "GET",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      // });

      const siteInfoResponse = {
        json: () =>
          Promise.resolve({
            damageWaiver: true,
            damageWaiverPercentage: 5,
            setupSurface: ["grass", "concrete", "indoor"],
          }),
      };

      const siteInfoData = (await siteInfoResponse.json()) as {
        damageWaiver: boolean;
        damageWaiverPercentage: number;
        setupSurface: string[];
      };
      dispatch(
        setDamageWaiver(
          siteInfoData?.damageWaiver || false,
          siteInfoData?.damageWaiverPercentage || 0,
        ),
      );
      dispatch(setSetupSurface(siteInfoData?.setupSurface || []));
    } catch (e) {
      captureException(e);
      console.error(e);
    }

    try {
      // const categoryResponse = await fetch(`${base_url}categories`, {
      //   method: "GET",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      // });
      // const productResponse = await fetch(`${base_url}products`, {
      //   method: "GET",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      // });

      const categoryResponse: { json: () => Promise<CategoryBackendResponse> } = {
        json: () =>
          Promise.resolve({
            categories: [
              {
                id: 7,
                display: false,
                name: "BestSellers",
                description: "",
                heroImage: "",
                altImages: [],
                slug: ""
              },
              {
                id: 1,
                display: false,
                name: "Bounce Combos",
                description:
                  "<p>We offer combo bounce house slides that will have things like a basketball hoop or slide built right inside with a bounce area! We specialize in bounce house rentals in Benton, Bryant, and Little Rock and will be happy to accommodate your party needs.</p>\r\n",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/avzgf3o5wuhhnt0cpv3xlape/Bounce Combos",
                altImages: [],
                slug: ""
              },
              {
                id: 2,
                display: true,
                name: "Obstacle Course",
                description:
                  "Have competitive races and lots of fun with these large inflatables.\n",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/umtc0ds508chmmu5gedesroi/Fire Island Obstacle.png",
                altImages: [],
                slug: ""
              },
              {
                id: 5,
                display: true,
                name: "Party Add-Ons",
                description: "Add some extra fun to any party!",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/v2uiq05xnkuf4n9cadlwi2nh/AxeThrowing2.jpeg",
                altImages: [],
                slug: ""
              },
              {
                id: 4,
                display: true,
                name: "Bounce Houses",
                description:
                  "The perfect focal point to addition to any event an unbeatable price!",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/nan1zhve9q5m2923ukpmqd89/IMG_2578 Medium.png",
                altImages: [
                  "https://static.dash.partyrentalplatform.com/1/kxb2aglk07wodws50k74a0nu/tableAndChair.png",
                ],
                slug: ""
              },
              {
                id: 6,
                display: true,
                name: "Tables and Chairs",
                description:
                  "Additional accessories you might need for your event.\n",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/upzzp4qsn1t3wherqnwkjx4h/tableAndChair.png",
                altImages: [],
                slug: ""
              },
              {
                id: 3,
                display: true,
                name: "Water Slide",
                description:
                  "Slip and slide into some fun with our Water Slides with Central Arkansas Inflatables\n",
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/lkwunssby9kjqvfa8fhrpjd1/SeaSide Good Pic.png",
                altImages: [],
                slug: ""
              },
            ],
          }),
      };

      const productResponse = {
        json: () =>
          Promise.resolve({
            products: [
              {
                id: 12,
                display: true,
                name: "Tropical XL Bounce House With Slide (Wet/Dry)",
                description:
                    "<table>\n  <tr>\n    <th>Age Group</th>\n    <td>3+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Summer Celebrations, Tropical-Themed Parties</td>\n  </tr>\n</table>\n<p>Escape to a paradise of play with the <strong>Tropical XL Bounce House With Slide</strong>, your all-in-one solution for endless fun, brought to you by <strong>Central Arkansas Inflatables</strong>. Ideal for children aged 3 and up, this vibrant playland combines the thrill of bouncing with the excitement of sliding, all wrapped up in a stunning tropical theme.</p>\n<p>Adorned in shades of blue, teal, and green, this bounce house evokes the spirit of the tropics, complete with palm trees and exotic patterns that invite kids to leap, bounce, and slide into a world of imaginative play. Whether used as a dry bounce house for a hopping good time or as a wet slide for a refreshing splash, it's perfect for any event or backyard party.</p>\n<p>Made from high-quality, durable materials, the Tropical XL Bounce House With Slide is designed for safety and longevity, ensuring a worry-free experience for parents and endless fun for kids. Its large size accommodates multiple children, making it an ideal centerpiece for birthdays, family gatherings, and tropical-themed celebrations.</p>\n<p>With <strong>Central Arkansas Inflatables</strong>, setting up your tropical oasis is a breeze. Our team will handle everything from delivery to setup and takedown, covering Saline County, Little Rock, Hot Springs, Sherwood, and beyond. We're committed to bringing the fun to you, making your next event an unforgettable island getaway.</p>\n<p>Transform your backyard into a tropical paradise with the Tropical XL Bounce House With Slide. It's more than just a bounce house; it's a ticket to an adventurous, tropical escape where every jump is a wave of joy!</p>",
                price: 275,
                quantity: 1,
                categoryId: 4,
                altImages: [
                  "https://static.dash.partyrentalplatform.com/1/se66cicxeuhwb0y3vaxfr6ch/IMG_2578 Medium.png",
                ],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/vsin21yrc51h6hkyc76puk1q/Tropical XL Wet/Dry Combo",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 23,
                display: true,
                name: "Electric XL Bounce House With Slide (Wet/Dry)",
                description:
                    "<h1>New Product!</h1>\n" +
                    "<table>\n" +
                    "  <tr>\n" +
                    "    <th>Age Group</th>\n" +
                    "    <td>3+</td>\n" +
                    "  </tr>\n" +
                    "  <tr>\n" +
                    "    <th>Best For</th>\n" +
                    "    <td>Summer Parties, Hot Days, Backyard Fun</td>\n" +
                    "  </tr>\n" +
                    "</table>\n" +
                    "<p>Get ready for electrifying fun with the <strong>Electric XL Bounce House With Slide</strong>, the ultimate addition to any summer gathering, brought to you by <strong>Central Arkansas Inflatables</strong>. Perfect for kids aged 3 and up, this vibrant bounce house offers a combination of jumping and sliding excitement, whether used as a water slide or dry.</p>\n" +
                    "<p>Dressed in eye-catching colors and bold patterns, the Electric XL Bounce House With Slide sparks the imagination, inviting children to bounce, leap, and slide their way through hours of entertainment. Whether it's a dry day of fun or a splashy adventure with a water slide, this bounce house with a slide is a must-have for any backyard party or event.</p>\n" +
                    "<p>Crafted from durable, high-quality materials, the Electric XL Bounce House With Slide ensures safety and longevity, giving parents peace of mind and kids endless enjoyment. Its spacious design can accommodate multiple children at once, making it the star attraction for birthdays, family get-togethers, and summer celebrations.</p>\n" +
                    "<p>With <strong>Central Arkansas Inflatables</strong>, setting up your electrifying fun zone is hassle-free. Our team manages everything from delivery to setup and takedown, serving Saline County, Benton, Bryant, Little Rock, Hot Springs, Sherwood, and more. We bring the party to you, ensuring an unforgettable, action-packed experience.</p>\n" +
                    "<p>Turn up the excitement and transform your backyard into a hub of fun with the Electric XL Bounce House With Slide. It's not just a bounce house; it's a source of endless laughter and memorable moments!</p>\n",
                price: 275,
                quantity: 1,
                categoryId: 4,
                altImages: [
                  "https://imagedelivery.net/6QiASA1pHsoYecw9egSmhw/2d3e7667-ba73-4a53-451f-2353600c0d00/public",
                    "https://imagedelivery.net/6QiASA1pHsoYecw9egSmhw/59010bd9-5105-48a5-0d0f-0bc66077a000/public"
                ],
                heroImage:
                    "https://imagedelivery.net/6QiASA1pHsoYecw9egSmhw/b7a33b19-b893-463b-a3d6-28267c301d00/public",
                ProductImageUpload: [],
                slug: ""
              },
              {
                "id": 28,
                "display": true,
                "name": "18ft Palm Spring Water Slide and Obstacle Course",
                "description": "<h3>NEW - 18ft Water Slide combined with a 31ft Obstacle Course! </h3>\n\n<table>\n  <tr>\n    <th>Age Group</th>\n    <td>5+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Big Events, Kids and Adults, Year Round Fun</td>\n  </tr>\n</table>\n<p>Introducing the 18' Palms Obstacle Slide — your ultimate inflatable adventure for tropical-themed festivities and summer escapades! Designed to ignite the thrill-seeker in every participant aged 5 and up, this inflatable masterpiece combines the excitement of an obstacle course with the joy of a slide, promising non-stop fun at any event.</p><p>Immerse yourself in the vibrant atmosphere of a palm-fringed oasis with this slide's striking design. The lush green palm trees and vibrant colors evoke the spirit of a tropical paradise, setting the perfect backdrop for hours of exhilarating play and friendly competition.</p><p>Standing tall at 18 feet, the Palms Obstacle Slide offers an adrenaline-pumping experience as participants race through obstacles and glide down its refreshing slide. The dual lanes ensure there's never a dull moment, encouraging friendly rivalries and laughter-filled challenges that elevate any gathering.</p><p>Crafted with top-tier materials for durability and safety, this inflatable guarantees a worry-free experience for all. Its robust construction and thoughtful design accommodate multiple users simultaneously, making it an ideal centerpiece for large gatherings and events.</p><p>At Central Arkansas Inflatables, we take care of everything from setup to dismantling, ensuring a seamless experience so you can focus on creating memories with friends and family. Serving Benton, Bryant, Haskell, Malvern, Saline County, Little Rock, Sherwood, Maumelle, Pulaski County and More! We bring the excitement of the Palms Obstacle Slide directly to your doorstep, transforming your backyard into a tropical adventure hub.</p><p>Make a splash at your next event with the 18' Palms Obstacle Slide — where every race, every laugh, and every slide down the palm-lined lanes brings a taste of paradise to your celebration. It's not just an inflatable; it's a gateway to unforgettable summer fun!</p><p></p><h4>Featuring:</h4><ul><li><p>18ft Dual Lane Slide</p></li><li><p>Can be used as a Water Slide or Dry</p></li><li><p>Dual Lane Obstacle Course</p></li><li><p>Deep Pool landing </p></li></ul></p>",
                "price": 400,
                "quantity": 1,
                "categoryId": 3,
                "subCategory": 2,
                "altImages": [
                  "https://static.dash.partyrentalplatform.com/1/tmh7gr50c8r9s0jyk8gu6ho5/palms_obstacle_inside.webp",
                ],
                "heroImage": "https://static.dash.partyrentalplatform.com/1/pku25x3uacudf5252mcal8xv/palms_obstacle_hero.webp",
                "ProductImageUpload": []
              },
              {
                "id": 20,
                "display": true,
                "name": "Bahama Breeze 18ft Dual Lane Water Slide",
                "description": "<strong>NEW</strong>\n\n<table>\n  <tr>\n    <th>Age Group</th>\n    <td>5+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Tropical-Themed Parties, Summer Fun</td>\n  </tr>\n</table>\n<p>Introducing the <strong>Bahama Breeze 18ft Dual Lane Water Slide</strong>—your ticket to a tropical paradise right in your backyard, brought to you by <strong>Central Arkansas Inflatables</strong>. Designed for thrill-seekers aged 5 and up, this towering water slide brings double the fun with its dual lanes, making it a must-have for any summer bash or tropical-themed party.</p>\n<p>Immerse yourself in the vibrant hues of the Bahamas with this water slide's eye-catching color palette. The cool blues and greens, accented with bright white, evoke the spirit of island life, inviting kids and adults alike to race down to the refreshing splash pool waiting below.</p>\n<p>At a majestic 18 feet, the Bahama Breeze offers an exhilarating drop that's sure to thrill every rider. The dual lanes add a competitive edge, perfect for races that amp up the excitement and laughter at any event. Whether it's a friendly competition or just a fun way to cool off, this water slide delivers on both fun and speed.</p>\n<p>Constructed with safety and durability in mind, the Bahama Breeze is made from high-quality materials, ensuring a safe and enjoyable experience for everyone. The generous size and sturdy design accommodate multiple participants, making it an ideal centerpiece for large gatherings and celebrations.</p>\n<p>With <strong>Central Arkansas Inflatables</strong>, planning your tropical getaway is a breeze. Our team handles all the logistics, from setup to takedown, allowing you to relax and enjoy the party. We proudly serve Little Rock, Benton, Bryant, Saline County, Hot Springs, Sherwood, and surrounding areas, bringing the island vibe straight to your doorstep.</p>\n<p>Dive into the fun with the Bahama Breeze 18ft Dual Lane Water Slide and transform your next event into an unforgettable tropical adventure. It's more than just a slide; it's a dual-lane dive into summer excitement!</p>",
                "price": 350,
                "quantity": 1,
                "categoryId": 3,
                "altImages": [
                  "https://static.dash.partyrentalplatform.com/1/wvfgxawt8y0i0omtqdc7ia5t/3.png",
                  "https://static.dash.partyrentalplatform.com/1/mt0jsq088cs7d7r5beodygp4/4.png"
                ],
                "heroImage": "https://static.dash.partyrentalplatform.com/1/cl8pooiaau61wslcsm1e02lw/1.png",
                "ProductImageUpload": [],
                slug: ""
              },
              {
                id: 5,
                display: true,
                name: "15ft SunDrop WaterSlide",
                description:
                    "<table>\n  <tr>\n    <th>Age Group</th>\n    <td>5+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Summer Events, Family Gatherings, Summer Birthdays</td>\n  </tr>\n</table>\n<p>Introducing the <strong>15' SunDrop WaterSlide</strong>, a cascade of color and fun, perfect for any summer celebration. With its striking combination of blue, red, and yellow, this waterslide offers a vibrant backdrop to outdoor events, family gatherings, and unforgettable summer birthdays.</p>\n<p>Designed for children aged 5 and up, the SunDrop WaterSlide brings a thrilling twist to traditional summer fun. Its height promises an exhilarating slide down into a refreshing splash pool, offering a cool respite on those hot summer days.</p>\n<p>The slide's colors and design, reminiscent of the bright and cheerful Sunny Day theme but without the sun motif, evoke the joy and freedom of summer. The bold colors not only stand out in any backyard or event space but also spark the imagination of every child who climbs to the top, ready to ride the wave of excitement down to the splashy end.</p>\n<p>Constructed with safety and durability in mind, this waterslide is the ideal addition to any summer festivity. The splash pool at the bottom ensures a soft and safe landing, making every slide as enjoyable as it is thrilling.</p>\n<p>At <strong>Central Arkansas Inflatables</strong>, we're committed to making your summer events hassle-free and filled with laughter. Our team will take care of the setup and takedown, ensuring the SunDrop WaterSlide is ready for action at your next outdoor celebration. We deliver the fun directly to you, serving Saline County, Little Rock, Hot Springs, Sherwood, and surrounding areas.</p>\n<p>Make a splash this summer with the 15' SunDrop WaterSlide. It's more than just a waterslide; it's an invitation to create joyful memories that will last a lifetime, one exhilarating slide at a time.</p>",
                price: 250,
                quantity: 1,
                categoryId: 3,
                altImages: [
                  "https://static.dash.partyrentalplatform.com/1/ezni0xshkneyx395ejghh45h/rain drop slide.png",
                ],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/z4gpadp4q8xit9tzq82vma30/better pic of rain drop slide.png",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 1,
                display: true,
                name: "15ft Sea Side WaterSlide",
                description:
                    "<table>\n  <tr>\n    <th>Age Group</th>\n    <td>3+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Summer Parties, Aquatic Adventures</td>\n  </tr>\n</table>\n<p>Make a splash at your next summer bash with the <strong>15' Sea Side WaterSlide</strong>! Brought to you by <strong>Central Arkansas Inflatables</strong>, this underwater-themed wonder is the perfect escape into a world of aquatic adventure for children aged 3 and up.</p>\n<p>Standing tall at 15 feet, this water slide is a beacon of fun, inviting little ones to slide down into the cool, refreshing water below. Adorned with playful dolphins at the crest and surrounded by a sea of charming creatures along the sides, it's an immersive experience that transports every child to a magical underwater world.</p>\n<p>Designed with safety and durability in mind, our Sea Side WaterSlide is constructed from top-quality materials, ensuring a safe and slippery slide every time. The vibrant colors and detailed sea-themed graphics make it not just a slide but a centerpiece for any outdoor celebration.</p>\n<p>With <strong>Central Arkansas Inflatables</strong>, the fun comes to you. Our team takes care of all the setup and takedown, so you can focus on enjoying the day. Available for delivery in Saline County, Little Rock, Hot Springs, Sherwood, and more, we're here to make your aquatic adventures unforgettable.</p>\n<p>Dive into the fun with the 15' Sea Side WaterSlide and watch as your backyard transforms into the coolest summer hotspot. It's not just a slide; it's an underwater voyage where every splash brings a smile!</p>",
                price: 250,
                quantity: 1,
                categoryId: 3,
                altImages: [
                  "https://static.dash.partyrentalplatform.com/1/q4fmmccib9hsnmtaua3p7kkk/SeaSlide.jpg",
                  "https://static.dash.partyrentalplatform.com/1/jxrmyasuozagibql82zadbon/SeaSide Good Pic.png",
                ],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/j7s2cmpyufgy1xx79584mmwa/SeaSlideSide.jpg",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 13,
                display: true,
                name: "46' Fire Wet/Dry Obstacle Course",
                description: "46 Feet long Obstacle Course!",
                price: 400,
                quantity: 1,
                categoryId: 2,
                altImages: [],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/pecrdt28korlefbetoqw6b41/46' Fire Wet/Dry Obstacle Course",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 14,
                display: true,
                name: "46' Wet/Dry Obstacle Course",
                description: "46 feet long obstacle course",
                price: 400,
                quantity: 1,
                categoryId: 2,
                altImages: [],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/q4msf02bi1dmws7g3eplctku/46' Wet/Dry Obstacle Course",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 9,
                display: true,
                name: "Circus Bounce House",
                description:
                    "<p>Introducing the Circus Bounce House, the ultimate source of entertainment and excitement for any event or party! This vibrant and colorful inflatable structure will transport children and adults alike into a world of circus magic.</p>\r\n\r\n<p>Designed with safety and fun in mind, the Circus Bounce House features high walls and a reinforced jumping area, ensuring hours of safe bouncing and laughter. The inflatable structure is crafted from durable and puncture-resistant materials, guaranteeing long-lasting enjoyment for all.</p>\r\n\r\n<p>Step inside the Circus Bounce House and be greeted by a whimsical circus-themed interior. With its eye-catching circus graphics, including clowns, acrobats, and animals, this bounce house creates an immersive experience that will ignite the imagination of all who enter.</p>\r\n\r\n<p>Perfect for birthdays, family gatherings, school events, or any occasion that calls for non-stop entertainment, the Circus Bounce House will keep children engaged and active for hours. Watch as they bounce, jump, and slide, experiencing the thrill of the circus right in their own backyard.</p>\r\n\r\n<p>Setting up the Circus Bounce House is a breeze, thanks to its compact size and easy-to-use inflation system. Simply plug it in, and within minutes, you&#39;ll have a fully inflated bounce house ready for action. When the fun is over, the deflate feature allows for quick and hassle-free storage.</p>\r\n\r\n<p>Safety is our top priority, and the Circus Bounce House is equipped with safety netting on all sides to prevent accidental falls. Additionally, the soft and cushioned floor provides a secure landing for those high-flying jumps.</p>\r\n\r\n<p>Make your next event unforgettable with the Circus Bounce House. It&#39;s not just a bounce house; it&#39;s a whole circus adventure waiting to happen. Book now and let the magic unfold!</p>\r\n10",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                    "https://static.dash.partyrentalplatform.com/1/npfcvg7cyquz8inc9zuvi1gb/Circus Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 22,
                display: true,
                name: "Generator 6550W",
                description:
                    "Generator for Rent! This generator can power 2 of our largest inflatables.",
                price: 60,
                quantity: 1,
                categoryId: 5,
                altImages: [],
                heroImage:
                    "https://www.harborfreight.com/media/catalog/product/cache/9fc4a8332f9638515cd199dd0f9238da/5/9/59205_W3.jpg",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 16,
                display: true,
                name: "Axe Throwing",
                description: "Axe throwing",
                price: 30,
                quantity: 1,
                categoryId: 5,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/d1qqlbt33hh1tnqc89huf1tc/Axe Throwing",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 24,
                display: true,
                name: "Corn Hole",
                description: "Corn Hole",
                price: 35,
                quantity: 1,
                categoryId: 5,
                altImages: [],
                heroImage:
                    "https://imagedelivery.net/6QiASA1pHsoYecw9egSmhw/3823f7d6-38c7-46c7-17e2-cebafecdd900/512",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 17,
                display: true,
                name: "Chair Rental (Heavy Duty)",
                description:
                  '<p>Upgrade Your Event with Our Chair Rental Service!</p>\r\n\r\n<p>Elevate the comfort and style of your special occasion with our chair rental options. From elegant weddings to corporate gatherings, our chairs offer both form and function, providing your guests with comfortable seating while enhancing the ambiance of your event. Make your reservation today and transform any space into an inviting and memorable setting with our chair rental service.</p>\r\n\r\n<p style="text-align: right;"><span style="font-size:12px;">Test</span></p>\r\n',
                price: 3.5,
                quantity: 48,
                categoryId: 6,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/bld2uh9nmojntyt66j5poglr/Chair Rental (Heavy Duty)",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 18,
                display: true,
                name: "6ft Table",
                description:
                  "<p>Transform Your Venue with Our Table Rental Service!</p>\r\n\r\n<p>Turn any space into an inviting and functional area with our table rental options. Whether you&#39;re planning a wedding, conference, or a social gathering, our wide range of tables will meet your needs. Our table rental service ensures quality and versatility, making your event setup a breeze. Elevate your event&#39;s atmosphere and practicality &ndash; reserve your tables today and let us provide the foundation for your perfect occasion.</p>\r\n",
                price: 12,
                quantity: 11,
                categoryId: 6,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/zwjqzh5sozhyzjlv0y6ox7st/6ft Table",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 6,
                display: true,
                name: "Red and Yellow Basketball Bounce House",
                description:
                  "<p>Introducing the vibrant and exciting Red and Yellow Basketball Bounce House! Get ready to take your basketball game to new heights with this inflatable wonderland.&nbsp;</p>\r\n\r\n<p>Designed with a striking combination of red and yellow, this bounce house will instantly catch the attention of kids and adults alike. The bold colors create an energetic atmosphere that will keep everyone engaged and entertained for hours.</p>\r\n\r\n<p>Equipped with a basketball hoop, this bounce house allows players to showcase their skills and shoot hoops while bouncing around. Whether you&#39;re practicing your layups or perfecting your three-point shots, this bounce house provides the perfect platform for basketball enthusiasts of all ages.</p>\r\n\r\n<p>Safety is our top priority, and this bounce house is no exception. It is constructed with durable materials that can withstand rigorous play, ensuring a safe and secure environment for all users. The high walls and reinforced seams provide added stability, giving parents peace of mind while their children enjoy endless bouncing fun.</p>\r\n\r\n<p>Setting up the Red and Yellow Basketball Bounce House is a breeze. It comes with an electric blower that inflates the structure in minutes, allowing you to quickly get the party started. The compact design makes it easy to transport and store, making it a versatile option for backyard parties, community events, or even indoor play centers.</p>\r\n\r\n<p>Whether you&#39;re hosting a birthday party, a school event, or simply looking to add some excitement to your backyard, the Red and Yellow Basketball Bounce House is the perfect choice. Get ready for endless fun, laughter, and friendly competition with this vibrant and thrilling inflatable masterpiece.</p>\r\n00",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/fujrdwyg6ewypceudkesrrb2/Red and Yellow Basketball Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 8,
                display: true,
                name: "Sports Bounce House",
                description:
                  "<p>Introducing the ultimate source of fun and excitement for sports enthusiasts - the Sports Bounce House! This inflatable masterpiece is designed to bring the thrill of sports right to your backyard or event.</p>\r\n\r\n<p>Made with high-quality materials, the Sports Bounce House is built to withstand hours of active play. Its vibrant colors and detailed sports-themed graphics create an immersive experience that will captivate kids and adults alike.</p>\r\n\r\n<p>Featuring a spacious jumping area, this bounce house provides ample room for energetic bouncing, jumping, and leaping. Whether it&#39;s a basketball game, soccer match, or football touchdown celebration, the Sports Bounce House is the perfect setting for endless active play and friendly competition.</p>\r\n\r\n<p>Safety is our top priority, which is why the Sports Bounce House is equipped with safety netting on all sides to ensure a secure environment. Parents can relax and enjoy peace of mind while their little ones engage in safe and supervised fun.</p>\r\n\r\n<p>Setting up the Sports Bounce House is a breeze. With its compact design and easy-to-use electric blower, you can have the party started in no time. It&#39;s perfect for birthday parties, family gatherings, school events, and any occasion that calls for an unforgettable sports-themed adventure.</p>\r\n\r\n<p>Don&#39;t miss out on the opportunity to bring the excitement of sports to your next event. Get the Sports Bounce House today and watch as laughter and joy fill the air!</p>\r\n",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/ccyxp1e2blpb1dzaswg691qo/Sports Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 10,
                display: true,
                name: "Jungle Jumper Bounce House",
                description:
                  "<p>Introducing the Jungle Jumper Bounce House, the ultimate adventure playground for your little ones! Let their imaginations run wild as they bounce, slide, and explore in this thrilling jungle-themed inflatable.</p>\r\n\r\n<p>Crafted with safety and durability in mind, the Jungle Jumper Bounce House is made from high-quality, puncture-resistant materials that can withstand hours of energetic play. Its spacious design provides ample room for multiple children to enjoy at once, making it perfect for birthday parties, family gatherings, or simply a fun-filled afternoon in the backyard.</p>\r\n\r\n<p>Featuring vibrant and eye-catching graphics of friendly jungle animals, this bounce house will transport your kids into a world of excitement and wonder. They can bounce alongside a playful monkey, slide down a slippery snake, or venture through the lush foliage of the jungle.</p>\r\n\r\n<p>With its easy setup and takedown, the Jungle Jumper Bounce House is hassle-free and convenient. The included electric blower inflates the structure in just minutes, and the deflation process is quick and simple, allowing you to spend more time enjoying the laughter and smiles of your children.</p>\r\n\r\n<p>Safety is our top priority, which is why the Jungle Jumper Bounce House includes sturdy mesh walls to keep your little adventurers secure while they bounce and play. The entrance is equipped with a safety netting feature, ensuring that children can enter and exit safely.</p>\r\n\r\n<p>Whether you&#39;re hosting a party or simply looking to provide endless entertainment for your kids, the Jungle Jumper Bounce House is the perfect addition to your backyard. It promotes active play, improves coordination, and encourages social interaction among children, all while creating unforgettable memories.</p>\r\n\r\n<p>Invest in the Jungle Jumper Bounce House today and watch as your children embark on a thrilling jungle adventure right in your own backyard!</p>\r\n",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/b93l1vxwc2wo54fj2evni12v/Jungle Jumper Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 11,
                display: true,
                name: "All American Bounce House",
                description:
                  "<p>Introducing the All American Bounce House - the ultimate source of fun and excitement for your next event! Whether you&#39;re hosting a birthday party, family gathering, or community event, this inflatable masterpiece is guaranteed to keep kids of all ages entertained for hours.</p>\r\n\r\n<p>Crafted with top-quality materials and designed with safety in mind, the All American Bounce House offers a spacious and secure environment for children to jump, bounce, and play to their heart&#39;s content. Its vibrant red, white, and blue color scheme adds a patriotic touch to any occasion, making it a perfect addition to Fourth of July celebrations or other patriotic-themed events.</p>\r\n\r\n<p>With its generous size, this bounce house provides ample space for multiple children to enjoy at once, promoting social interaction and active play. The sturdy walls and reinforced stitching ensure durability, allowing for endless hours of bouncing fun without any worries.</p>\r\n\r\n<p>Setting up the All American Bounce House is a breeze, thanks to its user-friendly design and included electric blower. In just a matter of minutes, you can have this inflatable wonder inflated and ready for action. And when the party is over, simply deflate and pack it away for convenient storage until your next event.</p>\r\n\r\n<p>Safety is our top priority, which is why the All American Bounce House features mesh netting on all sides, providing excellent visibility for parents while ensuring children stay securely inside. Additionally, the entrance ramp allows for easy entry and exit, minimizing the risk of accidents.</p>\r\n\r\n<p>Renting the All American Bounce House is a surefire way to make your event unforgettable. Not only will it keep the kids entertained, but it will also create lasting memories for everyone involved. So why wait? Bring the All American Bounce House to your next gathering and watch the fun unfold!</p>\r\n0",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/vltbew3gzqugkxnf3ec93hp0/All American Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 2,
                display: true,
                name: "Sunny Day Red And Blue Bounce House",
                description:
                  "<table>\n  <tr>\n    <th>Age Group</th>\n    <td>3+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Backyard Parties, Family Gatherings</td>\n  </tr>\n</table>\n<p>Dive into the vibrant world of the <strong>Sunny Day Red and Blue Bounce House</strong>, brought to you by <strong>Central Arkansas Inflatables</strong>, your trusted partner for unforgettable backyard fun. Perfect for children aged 3 and up, this bounce house combines safety, durability, and a bright, cheerful design to ensure endless laughter and joy.</p>\n<p>With a cheerful sun welcoming every little jumper at the entrance, our bounce house is more than just a play area—it's a catalyst for imagination and adventure. Crafted from commercial-grade materials, it's designed to keep your kids safe while they explore a world of fun.</p>\n<p>At Central Arkansas Inflatables, we take the stress out of party planning. Our service includes <strong>complete setup and takedown</strong>, handled by our skilled team, so you can focus on enjoying the moment. We proudly deliver to all of Saline County, including Benton and Bryant, as well as Little Rock, Hot Springs, Sherwood, and other nearby cities, ensuring that fun is never too far away.</p>\n<p>Turn your backyard into the ultimate destination for fun with the Sunny Day Red and Blue Bounce House. Let the sounds of laughter fill the air as your children create memories that will last a lifetime. Choose Central Arkansas Inflatables for a hassle-free experience that brings joy right to your doorstep.</p>",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/dh2xwevzvkc4a3ajz14zj16n/Sunny Day Red And Blue Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
              {
                id: 7,
                display: true,
                name: "Spooky Halloween Bounce House",
                description:
                  "<table>\n  <tr>\n    <th>Age Group</th>\n    <td>3+</td>\n  </tr>\n  <tr>\n    <th>Best For</th>\n    <td>Halloween Parties, Themed Events</td>\n  </tr>\n</table>\n<p>Step into a world of frightful fun with the <strong>Spooky Halloween Bounce House</strong>, presented by <strong>Central Arkansas Inflatables</strong>. Designed for little ghosts and goblins aged 3 and up, this bounce house is a thrilling addition to any Halloween celebration, combining the perfect blend of spookiness and endless bouncy joy.</p>\n<p>Drenched in vibrant shades of orange, purple, and green, this bounce house sets the stage for a spectacularly spooky party. With friendly ghosts, giggling goblins, and whimsical witches adorning its walls, it's the ultimate backdrop for memorable photos and joyous laughter.</p>\n<p>Constructed with the highest quality, commercial-grade materials, the Spooky Halloween Bounce House isn't just about fun; it's also about safety. You can rest easy knowing your little ones are enjoying themselves in a secure, durable play area.</p>\n<p>Leave the heavy lifting to us at <strong>Central Arkansas Inflatables</strong>. Our professional team will handle the setup and takedown, allowing you to focus on the festivities. Available for delivery throughout Saline County, Little Rock, Hot Springs, Sherwood, and surrounding areas, we ensure that your Halloween party is a hit, no matter where you are.</p>\n<p>Make this Halloween unforgettable with the Spooky Halloween Bounce House. Let the laughter of little monsters fill the air as they bounce to their heart's content, creating magical memories that will linger long after the candy has vanished.</p>",
                price: 150,
                quantity: 1,
                categoryId: 4,
                altImages: [],
                heroImage:
                  "https://static.dash.partyrentalplatform.com/1/dgylgdb67x8jyfbn4wi8kpjp/Spooky Halloween Bounce House",
                ProductImageUpload: [],
                slug: ""
              },
            ],
          }),
      };

      const categoryData: CategoryBackendResponse =
        await categoryResponse.json();
      const productData = await productResponse.json();

      categoryData.categories.forEach((category) => {
        category.slug = slugify(category.name, { lower: true });
      });

      productData.products.forEach((product) => {
        product.slug = slugify(product.name, { lower: true });
      });

      dispatch(
        getAllProducts({
          products: productData?.products || [],
          categories: categoryData?.categories || [],
        }),
      );
    } catch (e) {
      captureException(e);
      console.error(e);
    }
  };

  useEffect(() => {
    requestStoreInfo();
  }, []);

  return useRoutes(routes);
};
export default App;
